<template>
  <div class="row">
    <div class="col-md-6">
      <h3
        class="page-title-heading col-title"
        data-test="camera-edit-form-header"
      >
        {{ $localize('Details') }}
      </h3>
      <TextInputField
        id="cameraName"
        ref="cameraNameField"
        v-model="cameraName"
        :label="$localize('CameraName')"
        :validation-rules="validationRules?.cameraName"
        data-test-attribute="camera-name-input"
      />

      <TextInputField
        id="internalName"
        v-model="internalName"
        :tooltip="$localize('InternalNameTooltip')"
        :label="$localize('InternalName')"
        :validation-rules="validationRules?.internalName"
        data-test-attribute="camera-internal-name-input"
      />
      <TextInputField
        id="cameraMake"
        v-model="cameraMake"
        :label="$localize('CameraMake')"
        :validation-rules="validationRules?.cameraMake"
        data-test-attribute="camera-make-input"
      />
      <TextInputField
        id="cameraModel"
        v-model="cameraModel"
        :label="$localize('CameraModel')"
        :validation-rules="validationRules?.cameraModel"
        data-test-attribute="camera-model-input"
      />
      <text-area-field
        id="cameraDescription"
        v-model="cameraNotes"
        :label="$localize('CameraDescription')"
        :validation-rules="validationRules?.description"
        rows="7"
        data-test-attribute="camera-description-input"
      />
    </div>
    <div class="col-md-6">
      <h3
        class="page-title-heading col-title"
        data-test="camera-map-header"
      >
        {{ $localize('PlaceOnMap') }}
      </h3>
      <AddressLookupField
        v-model="cameraCoordinate"
        :label="$localize('AddressSearchTerm')"
        data-test="camera-address-search-input"
        @change="onAddressChanged"
      />

      <label class="form-label">{{ $localize('CameraMarkerInstructions') }}</label>
      <CameraDetailsMap
        v-model:coordinate="coordinate"
        v-model:camera-coordinate="cameraCoordinate"
        v-model:zoom="zoom"
        :hide-marker="hideMapMarker"
      />
    </div>
  </div>
  <ServerErrorList :errors="serverErrors" />
</template>

<script lang="ts" setup>
  import { onMounted, ref, watch } from 'vue';
  import CameraDetailsMap from '@/VueComponents/Cameras/CameraDetails/CameraDetailsMap.vue';
  import AddressLookupField from '@/VueComponents/SharedComponents/InputFields/AddressLookupField.vue';
  import TextInputField from '@/VueComponents/SharedComponents/InputFields/TextInputField.vue';
  import TextAreaField from '@/VueComponents/SharedComponents/InputFields/TextAreaField.vue';
  import MapCoordinate from '@/Types/mapCoordinate';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
  import CameraEditFormValidationRules from '@/VueComponents/Cameras/Models/cameraEditFormValidationRules';
  import { useForm } from 'vee-validate';
  import ServerErrorList from '@/VueComponents/SharedComponents/ErrorList/ServerErrorList.vue';

  const contextData = useContextDataStore();

  const props = defineProps<{
    serverErrors?: string[],
    validationRules?: CameraEditFormValidationRules,
    hideMapMarker?: boolean
  }>();

  const cameraName = defineModel<string>('cameraName');
  const internalName = defineModel<string>('internalName');
  const cameraMake = defineModel<string>('cameraMake');
  const cameraModel = defineModel<string>('cameraModel');
  const cameraNotes = defineModel<string>('cameraNotes');
  const cameraCoordinate = defineModel<MapCoordinate>('cameraCoordinate');

  const cameraNameField = ref();
  onMounted(() => {
    cameraNameField.value.focus();
  });


  const form = useForm({
    initialValues: {
      'cameraName': cameraName.value,
      'cameraModel': cameraModel.value,
      'cameraMake': cameraMake.value,
      'internalName': internalName.value,
      'cameraDescription': cameraNotes.value
    }
  });  

  if (!cameraCoordinate.value) {
    cameraCoordinate.value = getDefaultMapCoordinate();
  }

  const coordinate = ref(getDefaultMapCoordinate());

  watch(() => props.hideMapMarker, hide => {
    if (!hide) {
      coordinate.value = {
        longitude: cameraCoordinate.value!.longitude,
        latitude: cameraCoordinate.value!.latitude
      };
    }
  }, { immediate: true });

  const defaultZoomValue = contextData.portalSettings.defaultMapView.zoom ?? 17;
  const zoom = ref(defaultZoomValue);

  // Expose these refs to the test wrapper
  defineExpose({
    isValid,
    zoom,
    coordinate
  });

  function onAddressChanged(coord: MapCoordinate) {
    coordinate.value = coord;
    zoom.value = defaultZoomValue;
  }

  function getDefaultMapCoordinate() {
    if (contextData.userData.business.coordinate) {
      return contextData.userData.business.coordinate;
    }

    return contextData.portalSettings.defaultMapView.coordinate;
  }

  async function isValid() {
    return (await form.validate()).valid;
  }
</script>
<style scoped lang="scss">
  .col-title {
    margin-top: 20px;
  }
</style>